import React from 'react';
import { graphql } from 'gatsby';

import Button from '../components/Button';
import Hero from '../components/Hero';
import Showcase from '../components/Showcase';
import Slider from '../components/Slider';
import { DefaultLogoReel } from '../components/LogoReel';
import IndustryCard from '../components/IndustryCard';
import { SignupBreakpoint } from '../components/Breakpoint';
import SolutionCard from '../components/SolutionCard';
import Ratings from '../components/Ratings';
import SectionHero from '../components/SectionHero';
import SliderTestimonial from '../components/SliderTestimonial';
import Section from '../components/Section';
import Linegraphic from '../components/Linegraphic';
import USPCard from '../components/USPCard';
import USPCardRedesigned from '../components/USPCardRedesigned';

const personas = [
  {
    icon: 'CodeLineIcon',
    title: 'For Engineering Teams',
    description:
      'GraphCMS is the ultimate content platform that enables you to deliver ambitious applications at scale, API first, using your preferred frameworks.',
    theme: 'light',
    ctaTo: '/for-engineering',
    cta: 'GraphCMS for Engineering',
  },
  {
    icon: 'ListCheck2Icon',
    title: 'For Content Creators',
    description:
      'GraphCMS empowers content creators, editors, and marketing teams to create exceptional digital experiences in the way they’ve defined.',
    theme: 'pink',
    ctaTo: '/for-content-creators',
    cta: 'GraphCMS for Creators',
  },
  {
    icon: 'LineChartLineIcon',
    title: 'For Businesses',
    description:
      'Inflexible technologies keep your business from innovating. Integrate all your existing services to deliver content from a single source.',
    theme: 'royal',
    ctaTo: '/for-businesses',
    cta: 'GraphCMS for Businesses',
  },
];

const HomePageTemplate = ({
  data: { featureGrid, industries, ratings, solutions, testimonials },
  pageContext: { page },
}) => {
  return (
    <React.Fragment>
      <Section>
        <Hero
          {...page}
          alignment="left"
          explode
          items={[
            {
              icon: 'GRAPHQL',
              position: { top: '90px', left: '-30px' },
            },
            {
              icon: 'OMNICHANNEL',
              size: 'small',
            },
          ]}
        >
          <div className="grid gap-4 lg:grid-cols-2 lg:gap-6">
            <Button
              href="https://app.graphcms.com/signup"
              colour="pink"
              size="large"
            >
              Sign up for free &#8594;
            </Button>
            <Button
              href="/contact"
              appearance="minimal"
              colour="pink"
              size="large"
            >
              Contact sales
            </Button>
          </div>
        </Hero>
      </Section>
      <DefaultLogoReel className="mb-16 md:mb-24" />

      <section className="bg-whisper py-20">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="mx-auto space-y-6 text-center w-full">
              <h2 className="text-haiti font-bold leading-tight text-3xl md:text-5xl">
                Empowering teams with better content management
              </h2>
              <div className="max-w-3xl mx-auto">
                <p className="text-zodiac">
                  GraphCMS is the Headless CMS allowing you to build digital
                  experiences the way you envisioned them - with all your
                  backends, frontends, and services, working together in
                  harmony.
                </p>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-6 pt-12 md:pb-6 lg:pt-20">
            {personas.map((persona, index) => (
              <USPCardRedesigned key={index} {...persona} />
            ))}
          </div>
        </div>
      </section>

      <Section colour="dark" className="-mb-12 md:-mb-24">
        <SectionHero
          noHr
          title="The Content API for Market Leading Applications"
        />
        <Linegraphic />
      </Section>
      <Section colour="dark" size="small">
        <SectionHero
          noHr
          title="All you need"
          description="Freedom of expression and no templates or other nonsense. Build your digital products API-first and exactly how you envision them"
        />
        <div className="grid grid-cols-1 md:max-w-5xl mx-auto md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12">
          {featureGrid.nodes.map(USPCard)}
        </div>
      </Section>
      <SignupBreakpoint />
      <Section colour="light">
        <SectionHero
          noHr
          pretext="Deliver native omni-channel content across all platforms"
          title="Engage your customers where they are"
          ctas={[{ href: '/use-cases', label: 'View all use cases' }]}
        />
        <Showcase items={solutions.nodes} Component={SolutionCard} />
      </Section>
      <Section>
        <SectionHero
          noHr
          pretext="Rapidly transforming digital experiences across industries"
          title="GraphCMS enhances ROI across the board"
          ctas={[{ href: '/customers', label: 'View all industries' }]}
        />
        <Showcase items={industries.nodes} Component={IndustryCard} />
      </Section>
      <Section colour="light" container="narrower">
        <SectionHero title="Join over 40,000 teams in delivering better digital experiences" />
        <div className="my-12">
          <Ratings ratings={ratings.nodes} />
        </div>
        <Slider items={testimonials.nodes} Component={SliderTestimonial} />
      </Section>
    </React.Fragment>
  );
};

export const query = graphql`
  {
    featureGrid: allGraphCmsFeature(
      filter: { homepage: { eq: true } }
      sort: { fields: order, order: ASC }
    ) {
      nodes {
        id
        title
        description
        icon
        comingSoon
      }
    }
    industries: allGraphCmsIndustry(filter: { featured: { eq: true } }) {
      nodes {
        id
        slug
        linkPath
        title
        subTitle
        featuredContent
        featuredCaseStudies {
          id
          slug
          title
          coverImage {
            id
            url
            handle
            width
            height
          }
          externalUrl
          company {
            name
            url
            logo {
              id
              url
              handle
              width
              height
            }
          }
          customer {
            name
            title
            twitterHandle
            bio
            photo {
              id
              url
              handle
              width
              height
            }
          }
          linkPath
        }
      }
    }
    ratings: allGraphCmsRating {
      nodes {
        id
        platform
        url
        value
      }
    }
    solutions: allGraphCmsSolution(filter: { featured: { eq: true } }) {
      nodes {
        id
        slug
        linkPath
        title
        coverImage {
          id
          url
          handle
          width
          height
        }
        featuredContent
      }
    }
    testimonials: allGraphCmsTestimonial {
      nodes {
        company {
          caseStudy {
            linkPath
            slug
          }
          name
          url
          logo {
            id
            url
            handle
            width
            height
          }
        }
        author {
          name
          title
          twitterHandle
          bio
          photo {
            id
            url
            handle
            width
            height
          }
        }
      }
    }
  }
`;

export default HomePageTemplate;
